import React, { useRef, useState } from 'react';
import pendingPhotosService from '../services/pendingPhotosService';
import './QuickPhotoButton.css';

/**
 * QuickPhotoButton
 * ----------------
 * Bouton 📷 réutilisable pour "prise de photo rapide" avant saisie d'observation.
 *
 * Comportement :
 *  1. Click → ouvre la caméra arrière (input file capture="environment").
 *  2. Sélection photo → stocke en pending (IndexedDB, base64) + upload background.
 *  3. Mini-dialog "Saisir maintenant / Plus tard".
 *      - "Saisir maintenant" : appelle onOpenObservation(photo, context) (le parent ouvre ObservationModal).
 *      - "Plus tard" : reste sur l'écran, la photo est visible dans Étape 11 (section "Photos en attente").
 *
 * Props :
 *  - missionId         : string, requis.
 *  - context           : objet ({ step_origin, armoire_index, armoire_designation,
 *                                circuit_index, circuit_designation,
 *                                localisation, element, element_ref }).
 *  - onOpenObservation : (photo, context) => void   (déclenché si "Saisir maintenant").
 *  - label             : libellé bouton (défaut : "📷").
 *  - className         : classes additionnelles.
 *  - title             : tooltip natif.
 */
export default function QuickPhotoButton({
  missionId,
  context = {},
  onOpenObservation,
  label = '📷',
  className = '',
  title = 'Prendre une photo (puis saisir l\'observation)',
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pendingChoice, setPendingChoice] = useState(null); // photo créée en attente d'arbitrage

  const handleClick = () => {
    if (busy) return;
    if (inputRef.current) {
      inputRef.current.value = ''; // permet de re-sélectionner la même image
      inputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!missionId) {
      alert('Mission non identifiée — impossible d\'enregistrer la photo.');
      return;
    }

    // garde-fou taille (cohérent avec le reste de l'app)
    if (file.size > 8 * 1024 * 1024) {
      alert('⚠️ La photo est trop lourde (max 8 Mo).');
      return;
    }

    setBusy(true);
    try {
      const photo = await pendingPhotosService.add(missionId, file, context);
      setPendingChoice(photo);
    } catch (err) {
      console.error('[QuickPhotoButton] add error:', err);
      alert('❌ Impossible d\'enregistrer la photo.');
    } finally {
      setBusy(false);
    }
  };

  const handleChooseNow = () => {
    const photo = pendingChoice;
    setPendingChoice(null);
    if (typeof onOpenObservation === 'function') {
      onOpenObservation(photo, context);
    }
  };

  const handleChooseLater = () => {
    setPendingChoice(null);
    // pas d'alert intrusive : la photo apparaît dans Étape 11 → Photos en attente
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="quick-photo-input"
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        title={title}
        className={`quick-photo-btn ${className}`}
      >
        {busy ? '⏳' : label}
      </button>

      {pendingChoice && (
        <div className="quick-photo-modal-overlay" onClick={handleChooseLater}>
          <div className="quick-photo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quick-photo-modal-header">
              <span className="quick-photo-modal-title">Photo enregistrée</span>
            </div>

            <div className="quick-photo-modal-body">
              {pendingChoice.blob_base64 && (
                <img
                  src={pendingChoice.blob_base64}
                  alt="Aperçu"
                  className="quick-photo-modal-preview"
                />
              )}
              <p className="quick-photo-modal-text">
                Voulez-vous saisir l'observation maintenant, ou plus tard&nbsp;?
              </p>
              <p className="quick-photo-modal-hint">
                La photo est sauvegardée localement et s'envoie automatiquement
                dès qu'une connexion est disponible.
              </p>
            </div>

            <div className="quick-photo-modal-actions">
              <button
                type="button"
                className="quick-photo-btn-secondary"
                onClick={handleChooseLater}
              >
                Plus tard
              </button>
              <button
                type="button"
                className="quick-photo-btn-primary"
                onClick={handleChooseNow}
              >
                Saisir maintenant
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
